<template>
  <div class="filters-panel">
    <div class="filters-header" @click="toggleCollapsed">
      <div class="filters-header-content">
        <span class="filters-header-title">Фильтры</span>
        <div v-if="isCollapsed" class="filters-chips-inline">
          <div v-if="hasActiveFilters" class="chips-container">
            <span
              v-for="chip in activeFilterChips"
              :key="chip.key"
              class="filter-chip"
            >
              <span class="chip-label">{{ chip.label }}:</span>
              <span class="chip-value">{{ chip.value }}</span>
              <i
                class="pi pi-times chip-remove"
                @click.stop="removeFilter(chip)"
              />
            </span>
          </div>
          <div v-else class="no-filters-text">Фильтры не применены</div>
        </div>
      </div>
      <i
        :class="[
          'pi',
          isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up',
          'filters-header-icon',
        ]"
      ></i>
    </div>

    <div v-if="!isCollapsed" class="filters-content">
      <div class="filters-row">
        <div class="filter-field">
          <label class="filter-label">Бренд</label>
          <CatalogDataBaseInput
            :useIdOnly="true"
            selectType="brand"
            :isSegmentSetted="false"
            :modelValue="filter.brand"
            @update:modelValue="($event) => updateFilter('brand', $event)"
          ></CatalogDataBaseInput>
        </div>

        <div class="filter-field">
          <label class="filter-label">Сегмент</label>
          <CatalogDataBaseInput
            :useIdOnly="true"
            selectType="segment"
            :isSegmentSetted="false"
            :modelValue="filter.segment"
            @update:modelValue="($event) => updateFilter('segment', $event)"
          ></CatalogDataBaseInput>
        </div>

        <div class="filter-field">
          <label class="filter-label">Площадка</label>
          <CatalogDataBaseInput
            :useIdOnly="true"
            selectType="platform"
            :isSegmentSetted="false"
            :modelValue="filter.platform"
            @update:modelValue="($event) => updateFilter('platform', $event)"
          ></CatalogDataBaseInput>
        </div>

        <div class="filter-field">
          <label class="filter-label">Кабинет</label>
          <CatalogDataBaseInput
            :useIdOnly="true"
            selectType="ads_cabinet"
            :isSegmentSetted="false"
            :modelValue="filter.ads_cabinet"
            @update:modelValue="($event) => updateFilter('ads_cabinet', $event)"
          ></CatalogDataBaseInput>
        </div>
      </div>

      <div class="filters-row">
        <div class="filter-field date-range-field">
          <DateRangeSelect
            :modelValue="filter.date"
            @update:modelValue="($event) => updateFilter('date', $event)"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Статус</label>
          <MultiSelect
            v-model="filter.state"
            optionValue="id"
            :options="campaignsDictionariesStore.segmentsStatesForStatistics"
            optionLabel="display_name"
            placeholder="Выберите значение"
          />
        </div>

        <div class="filter-actions">
          <Button label="Применить" class="apply-button" @click="applyFilter" />
          <Button
            label="Сбросить"
            severity="secondary"
            outlined
            class="reset-button"
            @click="resetFilter"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import CatalogDataBaseInput from "@/widgets/forms/CatalogDataBaseInput.vue";
import CatalogDataSingleInput from "@/widgets/forms/CatalogDataSingleInput.vue";
import DateRangeSelect, {
  type DateRange,
} from "@/shared/forms/DateRangeSelect.vue";
import { useCampaignsDictionariesStore } from "@/store/dictionaries/campaigns";
import { useBrandsStore } from "@/store/catalog_data/brands";
import { useSegmentsDictionaryStore } from "@/store/segments/segments_dictionary";
import { useAdsCabinetsDictionariesStore } from "@/store/catalog_data/ads_cabinets";
import { usePlatformsDictionaryStore } from "@/store/catalog_data/platforms";
import {
  formatDateToYYYYMMDD,
  formatDateToDDMMYYYY,
} from "@/services/utils/DateParser";
import type { StatisticsFilter } from "@/store/expenses/statistics";

const props = defineProps<{
  appliedFilter?: StatisticsFilter | null;
}>();

const emit = defineEmits<{
  (e: "apply", filter: StatisticsFilter): void;
  (e: "reset"): void;
  (e: "removeFilter", filterKey: string, value?: string | number): void;
}>();

const campaignsDictionariesStore = useCampaignsDictionariesStore();
const brandsStore = useBrandsStore();
const segmentsStore = useSegmentsDictionaryStore();
const adsCabinetsStore = useAdsCabinetsDictionariesStore();
const platformsStore = usePlatformsDictionaryStore();

const isCollapsed = ref(true);

const initialFilter: StatisticsFilter = {
  brand: [] as string[],
  segment: [] as number[],
  ads_cabinet: [] as number[],
  date: null as DateRange | null,
  state: [] as number[],
  platform: [] as string[],
};

const filter = ref<StatisticsFilter>({ ...initialFilter });

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value;
};

const updateFilter = (key: string, value: any) => {
  (filter.value as any)[key] = value;
};

const applyFilter = () => {
  emit("apply", { ...filter.value });
};

const resetFilter = () => {
  filter.value = { ...initialFilter };
  emit("reset");
};

const removeFilter = (chip: {
  key: string;
  value: string | number;
  originalValue: string | number;
}) => {
  const applied = props.appliedFilter || filter.value;
  const updatedFilter = { ...applied };

  if (chip.key === "brand") {
    updatedFilter.brand = updatedFilter.brand.filter(
      (b) => b !== chip.originalValue
    );
  } else if (chip.key === "segment") {
    updatedFilter.segment = updatedFilter.segment.filter(
      (s) => s !== chip.originalValue
    );
  } else if (chip.key === "ads_cabinet") {
    updatedFilter.ads_cabinet = updatedFilter.ads_cabinet.filter(
      (c) => c !== chip.originalValue
    );
  } else if (chip.key === "state") {
    updatedFilter.state = updatedFilter.state.filter(
      (s) => s !== chip.originalValue
    );
  } else if (chip.key === "platform") {
    updatedFilter.platform = updatedFilter.platform.filter(
      (p) => p !== chip.originalValue
    );
  } else if (chip.key === "date") {
    updatedFilter.date = null;
  }

  filter.value = updatedFilter;
  emit("apply", updatedFilter);
};

const hasActiveFilters = computed(() => {
  const applied = props.appliedFilter || filter.value;
  return (
    (applied.brand && applied.brand.length > 0) ||
    (applied.segment && applied.segment.length > 0) ||
    (applied.ads_cabinet && applied.ads_cabinet.length > 0) ||
    (applied.state && applied.state.length > 0) ||
    (applied.platform && applied.platform.length > 0) ||
    (applied.date && (applied.date.start_date || applied.date.end_date))
  );
});

const activeFilterChips = computed(() => {
  const applied = props.appliedFilter || filter.value;
  const chips: Array<{
    key: string;
    label: string;
    value: string | number;
    originalValue: string | number;
  }> = [];

  if (applied.brand && applied.brand.length > 0) {
    applied.brand.forEach((brandPk) => {
      const brand = brandsStore.itemList.find((b) => b.brand_pk === brandPk);
      chips.push({
        key: "brand",
        label: "Бренд",
        value: brand ? brand.brand_name : brandPk,
        originalValue: brandPk,
      });
    });
  }

  if (applied.segment && applied.segment.length > 0) {
    applied.segment.forEach((segmentId) => {
      const segment = segmentsStore.itemList.find((s) => s.id === segmentId);
      chips.push({
        key: "segment",
        label: "Сегмент",
        value: segment ? segment.name : `ID: ${segmentId}`,
        originalValue: segmentId,
      });
    });
  }

  if (applied.ads_cabinet && applied.ads_cabinet.length > 0) {
    applied.ads_cabinet.forEach((cabinetId) => {
      const cabinet = adsCabinetsStore.itemsWithName.find(
        (c) => c.id === cabinetId
      );
      chips.push({
        key: "ads_cabinet",
        label: "Кабинет",
        value: cabinet ? cabinet.name : `ID: ${cabinetId}`,
        originalValue: cabinetId,
      });
    });
  }

  if (applied.platform && applied.platform.length > 0) {
    applied.platform.forEach((platformSlug) => {
      const platform = platformsStore.itemList.find(
        (p) => p.slug === platformSlug
      );
      chips.push({
        key: "platform",
        label: "Площадка",
        value: platform ? platform.name : platformSlug,
        originalValue: platformSlug,
      });
    });
  }

  if (applied.state && applied.state.length > 0) {
    applied.state.forEach((stateId) => {
      const state = campaignsDictionariesStore.segmentsStatesForStatistics.find(
        (s) => s.id === stateId
      );
      chips.push({
        key: "state",
        label: "Статус",
        value: state ? state.display_name : `ID: ${stateId}`,
        originalValue: stateId,
      });
    });
  }

  if (applied.date && (applied.date.start_date || applied.date.end_date)) {
    const startDate = applied.date.start_date
      ? formatDateToDDMMYYYY(
          applied.date.start_date instanceof Date
            ? applied.date.start_date
            : new Date(applied.date.start_date)
        )
      : "";
    const endDate = applied.date.end_date
      ? formatDateToDDMMYYYY(
          applied.date.end_date instanceof Date
            ? applied.date.end_date
            : new Date(applied.date.end_date)
        )
      : "";
    chips.push({
      key: "date",
      label: "Дата",
      value:
        startDate && endDate
          ? `${startDate} - ${endDate}`
          : startDate || endDate,
      originalValue: "date",
    });
  }

  return chips;
});

watch(
  () => props.appliedFilter,
  (newFilter) => {
    if (newFilter) {
      filter.value = { ...newFilter };
    }
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  if (campaignsDictionariesStore.segmentsStates.length === 0) {
    await campaignsDictionariesStore.getSegmentsStates();
  }

  const applied = props.appliedFilter;
  if (applied) {
    if (
      applied.brand &&
      applied.brand.length > 0 &&
      brandsStore.itemList.length === 0
    ) {
      if (!brandsStore.isLoadingInitial) {
        await brandsStore.getItems("", applied.brand, true, true, 0, 100, true);
      }
    }

    if (
      applied.segment &&
      applied.segment.length > 0 &&
      segmentsStore.itemList.length === 0
    ) {
      if (!segmentsStore.isLoadingInitial) {
        await segmentsStore.getItems(
          "",
          applied.segment.map(String),
          true,
          true,
          0,
          100,
          true
        );
      }
    }
    if (
      applied.ads_cabinet &&
      applied.ads_cabinet.length > 0 &&
      adsCabinetsStore.itemList.length === 0
    ) {
      if (!adsCabinetsStore.isLoadingInitial) {
        await adsCabinetsStore.getItems(
          "",
          applied.ads_cabinet.map(String),
          true,
          true,
          0,
          100,
          true
        );
      }
    }

    if (
      applied.platform &&
      applied.platform.length > 0 &&
      platformsStore.itemList.length === 0
    ) {
      if (!platformsStore.isLoadingInitial) {
        await platformsStore.getItems(
          "",
          applied.platform,
          true,
          true,
          0,
          100,
          true
        );
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.filters-panel {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  margin-top: 24px;
  margin-bottom: 0;

  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #f9fafb;
    }

    .filters-header-content {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
    }

    .filters-header-title {
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      white-space: nowrap;
    }

    .filters-header-icon {
      font-size: 12px;
      color: #6b7280;
      flex-shrink: 0;
    }

    .filters-chips-inline {
      flex: 1;
      min-width: 0;

      .chips-container {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .no-filters-text {
        color: #9ca3af;
        font-size: 14px;
      }

      .filter-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background-color: #f3f4f6;
        border-radius: 6px;
        font-size: 13px;
        color: #374151;

        .chip-label {
          font-weight: 500;
          color: #6b7280;
        }

        .chip-value {
          color: #111827;
        }

        .chip-remove {
          margin-left: 4px;
          cursor: pointer;
          color: #6b7280;
          font-size: 12px;
          transition: color 0.2s;

          &:hover {
            color: #ef4444;
          }
        }
      }
    }
  }

  .filters-content {
    padding: 20px;

    .filters-row {
      display: flex;
      gap: 16px;
      align-items: flex-end;
      flex-wrap: wrap;

      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }

    .filter-field {
      flex: 1;
      min-width: 180px;
      display: flex;
      flex-direction: column;

      &.date-range-field {
        flex: 2;
        min-width: 600px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 500;
        color: #6b7280;
        margin-bottom: 6px;
        line-height: 20px;
      }

      :deep(.p-multiselect),
      :deep(.p-dropdown) {
        width: 100%;
      }
    }

    .filter-actions {
      display: flex;
      gap: 12px;
      margin-left: auto;
      align-items: flex-end;

      .apply-button {
        min-width: 120px;
      }

      .reset-button {
        min-width: 120px;
      }
    }
  }
}
</style>
